'use client';

import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Navbar, Footer } from '@/components/home';
import {
  ArrowRight,
  CheckCircle,
  EnvelopeSimple,
  Paperclip,
  PenNib,
  User,
  X,
} from '@phosphor-icons/react';
import { useTranslations, useLocale } from '@/i18n';
import type { Id } from '@convex/_generated/dataModel';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const inputClass =
  'w-full h-14 pl-12 pr-4 rounded-2xl bg-[#F8F7F4] dark:bg-black border border-black/10 dark:border-white/10 focus:border-brand-red dark:focus:border-brand-gold focus:outline-none transition-colors text-black dark:text-white placeholder-zinc-500';

export default function TestimonyPage() {
  const submit = useMutation(api.testimonies.submit);
  const generateUploadUrl = useMutation(
    api.testimonies.generateAttachmentUploadUrl
  );
  const t = useTranslations();
  const locale = useLocale();
  const copy = t.testimony;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    title: '',
    body: '',
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    // Reset the input so picking the same file again still fires onChange.
    e.target.value = '';
    if (selected.length === 0) return;

    if (selected.some((file) => file.size > MAX_ATTACHMENT_BYTES)) {
      setError(copy.errorFileTooLarge);
      return;
    }
    if (files.length + selected.length > MAX_ATTACHMENTS) {
      setError(copy.errorTooManyFiles);
      return;
    }
    setError('');
    setFiles((prev) => [...prev, ...selected]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadAttachments = async () => {
    const uploaded: { storageId: string; name: string }[] = [];
    for (const file of files) {
      const uploadUrl = await generateUploadUrl();
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!response.ok) throw new Error('upload-failed');
      const { storageId } = await response.json();
      uploaded.push({ storageId, name: file.name });
    }
    return uploaded;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) return setError(copy.errorName);
    if (!formData.title.trim()) return setError(copy.errorTitle);
    if (!formData.body.trim()) return setError(copy.errorBody);

    setIsSubmitting(true);
    try {
      let attachments;
      if (files.length > 0) {
        setIsUploading(true);
        try {
          attachments = await uploadAttachments();
        } catch {
          setError(copy.errorUploadFailed);
          return;
        } finally {
          setIsUploading(false);
        }
      }

      await submit({
        name: formData.name.trim(),
        email: formData.email.trim() || undefined,
        title: formData.title.trim(),
        body: formData.body.trim(),
        source: 'web',
        attachments: attachments as
          | { storageId: Id<'_storage'>; name: string }[]
          | undefined,
      });
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : String(err);
      setError(
        raw.includes('Too many submissions') ? copy.errorTooMany : t.common.error
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <main className="min-h-screen bg-[#F8F7F4] dark:bg-black">
        <Navbar solid locale={locale} translations={t.nav} />

        <section className="min-h-[80vh] flex items-center justify-center px-6 py-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl w-full text-center"
          >
            <div className="mb-8 flex justify-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="h-24 w-24 bg-brand-gold/20 rounded-full flex items-center justify-center"
              >
                <CheckCircle weight="fill" className="h-12 w-12 text-brand-gold" />
              </motion.div>
            </div>

            <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 text-black dark:text-white leading-[0.9]">
              {copy.successTitle}{' '}
              <span className="text-brand-gold font-serif italic">
                {copy.successHighlight}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-zinc-600 dark:text-zinc-200 mb-12 font-light leading-relaxed">
              {copy.successMessage}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => {
                  setFormData({ name: '', email: '', title: '', body: '' });
                  setFiles([]);
                  setIsSubmitted(false);
                }}
                className="h-14 px-8 rounded-full bg-brand-red hover:bg-brand-red/90 text-white text-base font-bold shadow-lg"
              >
                {copy.shareAnother}
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = `/${locale}`)}
                className="h-14 px-8 rounded-full border-black/10 dark:border-white/10 text-black dark:text-white text-base"
              >
                {copy.homePage}
              </Button>
            </div>
          </motion.div>
        </section>

        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F8F7F4] dark:bg-black">
      <Navbar solid locale={locale} translations={t.nav} />

      <section className="relative py-32 px-6 overflow-hidden">
        <div className="container max-w-4xl mx-auto relative z-10">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="whileInView"
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-display font-bold mb-8 text-black dark:text-white leading-[0.9]">
              {copy.pageTitle}{' '}
              <span className="text-brand-red font-serif italic">
                {copy.pageTitleHighlight}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-zinc-600 dark:text-zinc-200 max-w-2xl mx-auto font-light leading-relaxed">
              {copy.description}
            </p>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="whileInView"
            transition={{ delay: 0.2 }}
            className="max-w-2xl mx-auto"
          >
            <form
              onSubmit={handleSubmit}
              className="bg-white dark:bg-zinc-900 rounded-[3rem] p-8 md:p-12 shadow-xl border border-black/5 dark:border-white/5"
            >
              <div className="relative mb-6">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={copy.namePlaceholder}
                  aria-label={copy.nameLabel}
                  required
                  className={inputClass}
                />
              </div>
              <div className="relative mb-6">
                <EnvelopeSimple className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={copy.emailPlaceholder}
                  className={inputClass}
                />
              </div>

              <div className="relative mb-6">
                <PenNib className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder={copy.titlePlaceholder}
                  maxLength={150}
                  required
                  className={inputClass}
                />
              </div>

              <div className="mb-8">
                <textarea
                  name="body"
                  value={formData.body}
                  onChange={handleInputChange}
                  placeholder={copy.bodyPlaceholder}
                  rows={8}
                  maxLength={5000}
                  required
                  className="w-full p-4 rounded-2xl bg-[#F8F7F4] dark:bg-black border border-black/10 dark:border-white/10 focus:border-brand-red dark:focus:border-brand-gold focus:outline-none transition-colors text-black dark:text-white placeholder-zinc-500 resize-none"
                />
              </div>

              <div className="mb-8">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFilesSelected}
                  className="sr-only"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex w-full items-center gap-3 h-14 px-5 rounded-2xl bg-[#F8F7F4] dark:bg-black border border-dashed border-black/20 dark:border-white/20 text-left transition-colors hover:border-brand-red dark:hover:border-brand-gold"
                >
                  <Paperclip className="h-5 w-5 text-zinc-400" />
                  <span className="text-black dark:text-white font-medium">
                    {copy.attachmentsAdd}
                  </span>
                  <span className="ml-auto text-xs text-zinc-500 dark:text-zinc-400">
                    {copy.attachmentsLimit}
                  </span>
                </button>

                {files.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {files.map((file, index) => (
                      <li
                        key={`${file.name}-${index}`}
                        className="flex items-center gap-3 rounded-xl bg-[#F8F7F4] dark:bg-black px-4 py-3"
                      >
                        <span className="truncate text-sm text-black dark:text-white">
                          {file.name}
                        </span>
                        <span className="ml-auto shrink-0 text-xs text-zinc-500 dark:text-zinc-400">
                          {formatSize(file.size)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          aria-label={`${copy.attachmentsRemove} ${file.name}`}
                          className="shrink-0 rounded-lg p-1 text-zinc-400 transition-colors hover:text-brand-red"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {error && (
                <div className="mb-6 p-4 bg-brand-red/10 rounded-2xl border border-brand-red/20">
                  <p className="text-brand-red dark:text-red-400 text-sm">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 rounded-full bg-brand-red hover:bg-brand-red/90 text-white text-base font-bold shadow-lg shadow-brand-red/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                      className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                    {isUploading ? copy.attachmentsUploading : copy.submitting}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {copy.submit}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>

              <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center mt-6">
                {copy.privacyNote}
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
